import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import PdfViewer from '@/components/PdfViewer';
import Seo from '@/components/Seo';
import { courses, getCourse } from '@/data/catalog';
import { useAuth } from '@/hooks/useAuth';
import { fetchAllAssignments, kindLabel, type CourseAssignment } from '@/lib/assignments';
import {
  fetchAllEnrollments,
  statusLabel,
  updateEnrollment,
  uploadRecognitionFile,
  type CourseEnrollment } from
'@/lib/enrollments';
import { fetchAllCourseProgress, type RemoteCourseProgress } from '@/lib/progress';
import { fetchAllSubmissions, latestSubmissionFor, submissionLabel, type AssignmentSubmission } from '@/lib/submissions';

function AdminProgress() {
  const { isAuthenticated, isAdmin } = useAuth();
  const [enrollments, setEnrollments] = useState<CourseEnrollment[]>([]);
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [submissions, setSubmissions] = useState<AssignmentSubmission[]>([]);
  const [courseProgress, setCourseProgress] = useState<RemoteCourseProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [openId, setOpenId] = useState('');
  const [recognitionBusyId, setRecognitionBusyId] = useState('');
  const [recognitionError, setRecognitionError] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchAllEnrollments(), fetchAllAssignments(), fetchAllSubmissions(), fetchAllCourseProgress()]).
    then(([people, tasks, work, progress]) => {
      if (cancelled) return;
      setEnrollments(people);
      setAssignments(tasks);
      setSubmissions(work);
      setCourseProgress(progress);
    }).
    catch((err) => {
      console.error('No se pudo cargar el avance:', err);
      if (!cancelled) setError('No se pudo cargar el avance de los alumnos. Inténtalo de nuevo.');
    }).
    finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const rows = useMemo(() => {
    return enrollments.
    filter((row) => row.status !== 'rejected').
    filter((row) => courseFilter === 'all' ? true : row.courseId === courseFilter).
    map((row) => {
      const courseTasks = assignments.filter((item) => item.courseId === row.courseId);
      const total = courseTasks.length;
      const doneTasks = courseTasks.filter((task) => latestSubmissionFor(submissions, task._id, row.userId));
      const graded = doneTasks.filter((task) => latestSubmissionFor(submissions, task._id, row.userId)?.status === 'graded');
      const percent = total > 0 ? Math.round(doneTasks.length / total * 100) : 0;
      const courseModules = getCourse(row.courseSlug)?.modules ?? [];
      const moduleRecord = courseProgress.find((item) => item.userId === row.userId && item.courseId === row.courseId);
      const modulesDone = moduleRecord?.completedModules.length ?? 0;
      const modulePercent = courseModules.length > 0 ? Math.round(modulesDone / courseModules.length * 100) : 0;
      return {
        row,
        courseTasks,
        doneTasks,
        gradedCount: graded.length,
        total,
        percent,
        modulesDone,
        moduleTotal: courseModules.length,
        modulePercent
      };
    });
  }, [assignments, courseFilter, courseProgress, enrollments, submissions]);

  async function handleRecognitionUpload(rowId: string, file: File) {
    setRecognitionError((prev) => ({ ...prev, [rowId]: '' }));
    setRecognitionBusyId(rowId);
    try {
      const uploaded = await uploadRecognitionFile(file);
      const updated = await updateEnrollment(rowId, {
        recognitionUrl: uploaded.url,
        recognitionName: uploaded.name
      });
      setEnrollments((prev) => prev.map((item) => item._id === rowId ? updated : item));
    } catch (err) {
      console.error('No se pudo subir el reconocimiento:', err);
      setRecognitionError((prev) => ({
        ...prev,
        [rowId]: err instanceof Error ? err.message : 'No se pudo subir el reconocimiento.'
      }));
    } finally {
      setRecognitionBusyId('');
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/acceso?next=/admin/avance" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/panel" replace />;
  }

  return (
    <>
      <Seo title="Avance de alumnos" description="Consulta el avance de cada alumno y los assignments que ya ha completado en HFCursos." />
      <section className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <p className="uppercase tracking-[0.28em] text-[#C41E3A] text-sm font-bold">Gestión</p>
        <h1 className="mt-3 font-['Fraunces'] text-4xl">Avance de alumnos</h1>
        <p className="mt-3 max-w-2xl text-sm text-[#1E3A5F]/70">
          Porcentaje de assignments entregados y listado de tareas ya completadas. El avance se calcula sobre las actividades publicadas de cada curso.
        </p>

        <div className="mt-6 flex flex-wrap gap-3 text-sm">
          <Link to="/admin" className="underline">
            Solicitudes
          </Link>
        </div>

        <label className="mt-8 block text-xs uppercase tracking-wide text-[#1E3A5F]/50" htmlFor="filtro-curso">
          Curso
        </label>
        <select
          id="filtro-curso"
          value={courseFilter}
          onChange={(event) => setCourseFilter(event.target.value)}
          className="mt-2 w-full max-w-md border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm outline-none">

          <option value="all">Todos los cursos</option>
          {courses.map((course) =>
          <option key={course.id} value={course.id}>
              {course.title}
            </option>
          )}
        </select>

        {error ? <p className="mt-4 text-sm text-[#C41E3A]">{error}</p> : null}

        {loading ?
        <p className="mt-10 text-sm text-[#1E3A5F]/60">Cargando avance…</p> :
        rows.length === 0 ?
        <p className="mt-10 border border-dashed border-[#1E3A5F]/20 p-8 text-sm text-[#1E3A5F]/65">
            Aún no hay alumnos en esta vista.
          </p> :

        <ul className="mt-8 space-y-4">
            {rows.map(({ row, courseTasks, doneTasks, gradedCount, total, percent, modulesDone, moduleTotal, modulePercent }) => {
            const open = openId === row._id;
            return (
              <li key={row._id} className="border border-[#1E3A5F]/10 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-[#C41E3A]">{statusLabel(row.status)}</p>
                      <h2 className="mt-1 font-['Fraunces'] text-2xl">{row.fullName}</h2>
                      <p className="mt-1 text-sm text-[#1E3A5F]/60">{row.email}</p>
                      <p className="mt-2 text-sm">{row.courseTitle}</p>
                      <p className="mt-2 text-xs text-[#1E3A5F]/50">
                        Avance del curso: {modulesDone} de {moduleTotal} sesiones ({modulePercent}%)
                      </p>
                      <p className="mt-1 text-xs text-[#1E3A5F]/50">
                        {doneTasks.length} de {total} assignments entregados
                        {total > 0 ? ` · ${gradedCount} calificados` : ''}
                      </p>
                    </div>
                    <Link to={`/panel/${row.courseSlug}`} className="text-sm underline">
                      Abrir curso
                    </Link>
                  </div>
                  <p className="mt-4 text-xs uppercase tracking-wide text-[#1E3A5F]/45">Sesiones ({modulePercent}%)</p>
                  <div className="mt-1 h-1.5 bg-[#1E3A5F]/10" aria-hidden="true">
                    <div className="h-full bg-[#1E3A5F]" style={{ width: `${modulePercent}%` }} />
                  </div>
                  <p className="mt-3 text-xs uppercase tracking-wide text-[#1E3A5F]/45">Assignments ({percent}%)</p>
                  <div className="mt-1 h-1.5 bg-[#1E3A5F]/10" aria-hidden="true">
                    <div className="h-full bg-[#C41E3A]" style={{ width: `${percent}%` }} />
                  </div>
                  <button
                  type="button"
                  className="mt-4 text-sm underline"
                  onClick={() => setOpenId(open ? '' : row._id)}
                  aria-expanded={open}>

                    {open ? 'Ocultar assignments' : 'Ver assignments completados'}
                  </button>
                  {open ?
                <ul className="mt-4 space-y-2">
                      {courseTasks.length === 0 ?
                  <li className="text-sm text-[#1E3A5F]/60">Este curso aún no tiene assignments publicados.</li> :

                  courseTasks.map((task) => {
                    const work = latestSubmissionFor(submissions, task._id, row.userId);
                    return (
                      <li key={task._id} className="border border-[#1E3A5F]/10 p-3">
                              <p className="text-xs uppercase tracking-wide text-[#C41E3A]">
                                {kindLabel(task.kind)} · {work ? submissionLabel(work.status) : 'Pendiente'}
                                {work?.score != null ? ` · nota ${work.score}` : ''}
                              </p>
                              <p className="mt-1 text-sm">{task.title}</p>
                              {work?.fileUrl ?
                        <a href={work.fileUrl} target="_blank" rel="noreferrer" className="mt-1 inline-block text-sm underline">
                                  Ver entrega{work.fileName ? `: ${work.fileName}` : ''}
                                </a> :
                        null}
                            </li>);

                  })
                  }
                    </ul> :
                null}
                  <div className="mt-5 border-t border-[#1E3A5F]/10 pt-4">
                    <p className="text-xs uppercase tracking-wide text-[#1E3A5F]/50">Reconocimiento del alumno</p>
                    {row.recognitionUrl ?
                  /\.pdf$/i.test(row.recognitionName || row.recognitionUrl) ?
                  <PdfViewer src={row.recognitionUrl} title={`Reconocimiento de ${row.fullName}`} /> :

                  <div
                    className="relative mt-3 overflow-hidden border border-[#1E3A5F]/15 bg-[#EDE6D9]"
                    onContextMenu={(event) => event.preventDefault()}>

                          <img
                      src={row.recognitionUrl}
                      alt={`Reconocimiento de ${row.fullName}`}
                      className="max-h-[70vh] w-full select-none object-contain"
                      draggable={false} />

                          <div className="pointer-events-none absolute right-2 top-2 bg-[#F4EFE6]/90 px-2 py-1 text-[10px] uppercase tracking-wide text-[#1E3A5F]/55">
                            Sin descarga
                          </div>
                        </div> :


                  <p className="mt-2 text-sm text-[#1E3A5F]/60">Aún no se ha subido un reconocimiento para este alumno.</p>
                  }
                    <label className="mt-3 inline-block cursor-pointer border border-[#1E3A5F] px-3 py-2 text-sm">
                      {recognitionBusyId === row._id ?
                    'Subiendo…' :
                    row.recognitionUrl ?
                    'Reemplazar reconocimiento' :
                    'Subir reconocimiento'}
                      <input
                      type="file"
                      accept="application/pdf,image/png,image/jpeg"
                      className="hidden"
                      disabled={recognitionBusyId === row._id}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (file) handleRecognitionUpload(row._id, file);
                      }} />

                    </label>
                    {recognitionError[row._id] ?
                  <p className="mt-2 text-sm text-[#C41E3A]">{recognitionError[row._id]}</p> :
                  null}
                  </div>
                </li>);

          })}
          </ul>
        }
      </section>
    </>);

}

export default AdminProgress;